import React, { Fragment } from "react";

interface WizardStepItem {
  id: string;
  label: string;
}

interface WizardStepsProps {
  items: WizardStepItem[];
  activeId: string;
  isUnlocked: (id: string) => boolean;
  onSelect: (id: string) => void;
}

/** Altura da faixa onde ficam círculo e trilho (alinha linha ao centro da bolinha) */
const STEP_TRACK_H = "h-9 sm:h-12";

export default function WizardSteps({
  items,
  activeId,
  isUnlocked,
  onSelect,
}: WizardStepsProps) {
  const currentIdx = Math.max(
    0,
    items.findIndex((i) => i.id === activeId)
  );

  return (
    <div className="mx-auto mb-8 mt-6 w-full max-w-4xl px-1 sm:px-2">
      <div className="flex w-full items-start justify-between">
        {items.map((item, index) => {
          const active = activeId === item.id;
          const unlocked = isUnlocked(item.id);
          const completed = unlocked && index < currentIdx;
          const locked = !unlocked;

          const circleClass = [
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold tabular-nums transition-colors duration-200 sm:h-12 sm:w-12 sm:text-base",
            active &&
              "border-orange-500 bg-orange-500 text-white",
            !active &&
              completed &&
              "border-sky-500 bg-sky-50 text-sky-800",
            !active && !completed && unlocked && "border-slate-300 bg-white text-slate-700 hover:border-slate-400",
            locked && "border-slate-200 bg-slate-100 text-slate-600",
          ]
            .filter(Boolean)
            .join(" ");

          const labelClass = [
            "mt-2 max-w-[88px] text-center text-[9px] font-bold uppercase leading-tight tracking-wide text-slate-600 sm:max-w-[96px] sm:text-[10px]",
            active && "text-orange-600",
            !active && completed && "text-sky-700",
            !active && !completed && unlocked && "text-slate-700",
          ]
            .filter(Boolean)
            .join(" ");

          const segmentFilled = index < currentIdx;

          return (
            <Fragment key={item.id}>
              {index > 0 && (
                <div
                  className={`flex min-w-0 flex-1 ${STEP_TRACK_H} items-center px-1 sm:px-2`}
                  aria-hidden
                >
                  <div
                    className={`h-0.5 w-full rounded-full ${segmentFilled ? "bg-sky-400" : "bg-slate-200"}`}
                  />
                </div>
              )}
              <div className="flex w-[56px] shrink-0 flex-col items-center sm:w-20">
                <div
                  className={`flex w-full ${STEP_TRACK_H} items-center justify-center`}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(item.id)}
                    disabled={!unlocked}
                    className={[
                      circleClass,
                      !unlocked ? "cursor-not-allowed" : "cursor-pointer",
                    ].join(" ")}
                  >
                    {index + 1}
                  </button>
                </div>
                <span className={labelClass}>{item.label}</span>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
